import React, { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Paper,
  Chip,
  CircularProgress,
  alpha,
  useTheme,
} from '@mui/material'
import { FiImage, FiVideo, FiClock, FiDownload } from 'react-icons/fi'

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'

const extractFilename = url => {
  if (!url || typeof url !== 'string') return ''
  const parts = url.split('?')[0].split('/').filter(Boolean)
  return parts[parts.length - 1] || ''
}

const formatTimestamp = timestamp => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  return `${diffDays}d ago`
}

const AuthPreview = React.memo(({ previewType, item, token, apiBaseUrl }) => {
  const [src, setSrc] = useState(null)
  const theme = useTheme()

  useEffect(() => {
    let objectUrl
    let cancelled = false

    const fetchPreview = async () => {
      // If we already have a blob URL and the item hasn't changed, don't refetch
      if (src && !cancelled) return

      try {
        if (!token) return
        let url = ''
        if (previewType === 'image' && item.file_urls && item.file_urls.length > 0) {
          const filename = extractFilename(item.file_urls[0])
          if (!filename) return
          url = `${apiBaseUrl}/generate/image/view/${filename}`
        } else if (previewType === 'video' && item.video_url) {
          const filename = extractFilename(item.video_url)
          if (!filename) return
          url = `${apiBaseUrl}/video/view/${filename}`
        } else {
          return
        }

        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) throw new Error(`Preview fetch failed`)

        const blob = await res.blob()
        objectUrl = URL.createObjectURL(blob)
        if (!cancelled) setSrc(objectUrl)
      } catch (e) {
        console.error('Preview error:', e)
      }
    }

    fetchPreview()

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
    // We strictly control dependencies to avoid loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.file_urls, item.video_url, token, apiBaseUrl, previewType])

  if (!src) {
    return (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          color: 'text.disabled',
        }}
      >
        {previewType === 'image' ? <FiImage size={32} /> : <FiVideo size={32} />}
      </Box>
    )
  }

  if (previewType === 'image') {
    return (
      <Box
        component="img"
        src={src}
        alt={item.prompt || 'Generated image'}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          transition: 'transform 0.3s ease',
          '.MuiCard-root:hover &': {
            transform: 'scale(1.05)',
          },
        }}
        loading="lazy"
      />
    )
  }

  return (
    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
      <Box
        component="video"
        src={src}
        muted
        playsInline
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 48,
          height: 48,
          bgcolor: alpha(theme.palette.primary.main, 0.9),
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          backdropFilter: 'blur(4px)',
          pointerEvents: 'none',
        }}
      >
        <FiVideo size={24} />
      </Box>
    </Box>
  )
})

const GenerationHistory = ({
  items = [],
  type = 'image',
  onItemClick,
  isLoading = false,
  token = null,
}) => {
  const theme = useTheme()

  const handleDownload = async (e, item) => {
    e.stopPropagation()
    try {
      if (type === 'image' && item.file_urls?.length > 0) {
        for (const rawUrl of item.file_urls) {
          const filename = extractFilename(rawUrl)
          if (!filename) continue
          const url = `${API_BASE_URL}/generate/image/download/${filename}`
          const res = await fetch(url, {
            headers: { Authorization: `Bearer ${token}` },
          })
          if (!res.ok) continue
          const blob = await res.blob()
          const objectUrl = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = objectUrl
          link.download = filename || 'image.png'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
          await new Promise(resolve => setTimeout(resolve, 100))
        }
      } else if (type === 'video' && item.video_url) {
        const filename = extractFilename(item.video_url)
        if (!filename) return
        const downloadUrl = `${API_BASE_URL}/video/download/${filename}`
        const res = await fetch(downloadUrl, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) return
        const blob = await res.blob()
        const objectUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = objectUrl
        link.download = filename || 'video.mp4'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
      }
    } catch (error) {
      console.error('Download error:', error)
    }
  }

  if (isLoading) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mt: 4,
          borderRadius: 3,
          border: '2px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          mb={2}
          pb={1.5}
          borderBottom={1}
          borderColor="divider"
        >
          <Typography
            variant="h6"
            fontWeight="700"
            color="text.primary"
            display="flex"
            alignItems="center"
            gap={1}
          >
            {type === 'image' ? <FiImage /> : <FiVideo />}
            Recenti {type === 'image' ? 'Immagini' : 'Video'}
          </Typography>
        </Box>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          py={5}
          gap={2}
          color="text.secondary"
        >
          <CircularProgress size={24} />
          <Typography fontWeight={500}>Caricamento...</Typography>
        </Box>
      </Paper>
    )
  }

  if (!items || items.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mt: 4,
          borderRadius: 3,
          border: '2px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          mb={2}
          pb={1.5}
          borderBottom={1}
          borderColor="divider"
        >
          <Typography
            variant="h6"
            fontWeight="700"
            color="text.primary"
            display="flex"
            alignItems="center"
            gap={1}
          >
            {type === 'image' ? <FiImage /> : <FiVideo />}
            Recenti {type === 'image' ? 'Immagini' : 'Video'}
          </Typography>
        </Box>
        <Box textAlign="center" py={5} color="text.secondary" fontWeight={500}>
          Nessun {type === 'image' ? 'immagine' : 'video'} recente
        </Box>
      </Paper>
    )
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mt: 4,
        borderRadius: 3,
        border: '2px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2.5}
        pb={1.5}
        borderBottom={2}
        borderColor="divider"
      >
        <Typography
          variant="h6"
          fontWeight="700"
          color="text.primary"
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
        >
          <Box component="span" sx={{ color: 'primary.main', display: 'flex' }}>
            {type === 'image' ? <FiImage size={22} /> : <FiVideo size={22} />}
          </Box>
          Recenti {type === 'image' ? 'Immagini' : 'Video'}
        </Typography>

        <Chip
          label={items.length}
          variant="outlined"
          color="primary"
          sx={{
            fontWeight: 'bold',
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            border: `2px solid ${theme.palette.divider}`,
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 2,
        }}
      >
        {items.map((item, index) => (
          <Card
            key={index}
            onClick={() => onItemClick && onItemClick(item)}
            variant="outlined"
            sx={{
              border: '2px solid',
              borderColor: 'divider',
              borderRadius: 3,
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              overflow: 'hidden',
              '&:hover': {
                borderColor: 'primary.main',
                transform: 'translateY(-4px)',
                boxShadow: `0 10px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
              },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                pt: '75%',
                bgcolor: 'action.hover',
                overflow: 'hidden',
              }}
            >
              <AuthPreview
                previewType={type}
                item={item}
                token={token}
                apiBaseUrl={API_BASE_URL}
              />

              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background:
                    'linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent)',
                  p: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                }}
              >
                <Chip
                  icon={<FiClock size={12} />}
                  label={formatTimestamp(item.timestamp)}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(0,0,0,0.4)',
                    color: 'white',
                    backdropFilter: 'blur(4px)',
                    height: 24,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: 'white' },
                    border: 'none',
                  }}
                />

                <IconButton
                  onClick={e => handleDownload(e, item)}
                  size="small"
                  title={type === 'image' ? `Scarica` : 'Scarica video'}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.9),
                    color: 'white',
                    borderRadius: 2,
                    backdropFilter: 'blur(4px)',
                    '&:hover': {
                      bgcolor: 'primary.main',
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  <FiDownload size={16} />
                </IconButton>
              </Box>
            </Box>

            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography
                variant="body2"
                fontWeight={500}
                color="text.primary"
                sx={{
                  mb: 1,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.3,
                  minHeight: '2.6em',
                }}
              >
                {item.prompt || 'Nessun prompt'}
              </Typography>

              <Box display="flex" gap={0.75} flexWrap="wrap">
                <Chip
                  label={item.model || 'N/A'}
                  size="small"
                  sx={{
                    borderRadius: 1.5,
                    height: 22,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    bgcolor: 'action.hover',
                    color: 'text.secondary',
                    border: '1px solid',
                    borderColor: 'divider',
                    textTransform: 'capitalize',
                  }}
                />
                {item.aspect_ratio && (
                  <Chip
                    label={item.aspect_ratio}
                    size="small"
                    sx={{
                      borderRadius: 1.5,
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      bgcolor: 'action.hover',
                      color: 'text.secondary',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                )}
                {type === 'image' && item.file_urls && item.file_urls.length > 1 && (
                  <Chip
                    label={`+${item.file_urls.length - 1}`}
                    size="small"
                    sx={{
                      borderRadius: 1.5,
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      bgcolor: 'action.hover',
                      color: 'text.secondary',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Paper>
  )
}

export default GenerationHistory
